/**
 * RegolePage — vista read-only del regolamento corrente.
 *
 * Tutti gli utenti autenticati vedono qui le regole della stagione attiva.
 * Per modificarle: solo admin via /admin/regolamento.
 */

import { useStagioneCorrente, useRegolamento } from "../../hooks/useStagione";
import { MobileShell } from "../shell/MobileShell";
import { AvvisoMantra } from "../common/AvvisoMantra";
import { etichetta } from "../common/etichette";
import { conSegno, etichetteFasce } from "../common/fasce";
import { NOME_BONUS_DIFENSIVO, NOME_MODALITA_PRO, sigleRuoli } from "../common/nomenclatura";
import { tr } from "../../i18n";

const EVENTO_LABEL: Record<string, string> = {
  gol: tr("Gol"),
  gol_portiere: tr("Gol portiere"),
  assist: tr("Assist"),
  ammonizione: tr("Ammonizione"),
  espulsione: tr("Espulsione"),
  autogoal: tr("Autogol"),
  rigore_segnato: tr("Rigore segnato"),
  rigore_sbagliato: tr("Rigore sbagliato"),
  rigore_parato: tr("Rigore parato"),
  portiere_imbattuto: tr("Portiere imbattuto"),
  gol_vittoria: tr("Gol vittoria"),
  gol_pareggio: tr("Gol pareggio"),
  gol_subito_portiere: tr("Gol subito (portiere)"),
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card space-y-2">
      <h3 className="font-display text-lg text-accent">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-primary/10 last:border-b-0 text-sm">
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg font-mono">{value}</span>
    </div>
  );
}

export function RegolePage() {
  const { data: stagione, isLoading: loadingStag } = useStagioneCorrente();
  const { data: reg, isLoading: loadingReg } = useRegolamento(stagione?.id ?? null);

  if (loadingStag) {
    return (
      <MobileShell logoSuffix="REGOLE">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Regole")}</h2>
        <div className="card text-fg-muted">{tr("Caricamento…")}</div>
      </MobileShell>
    );
  }

  if (!stagione) {
    return (
      <MobileShell logoSuffix="REGOLE">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Regole")}</h2>
        <div className="card text-fg-muted text-center p-8">
          <div className="text-3xl mb-2">📜</div>
          <div>{tr("Nessuna stagione attiva.")}</div>
          <div className="text-xs mt-2">
            {tr("Le regole appariranno qui quando l'admin avrà creato e avviato una stagione.")}
          </div>
        </div>
      </MobileShell>
    );
  }

  if (loadingReg) {
    return (
      <MobileShell logoSuffix="REGOLE">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Regole")}</h2>
        <div className="card text-fg-muted">{tr("Caricamento regolamento…")}</div>
      </MobileShell>
    );
  }

  if (!reg) {
    return (
      <MobileShell logoSuffix="REGOLE">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Regole")}</h2>
        <div className="card text-fg-muted text-center p-8">
          <div className="text-3xl mb-2">📜</div>
          <div>Regolamento non ancora configurato per {stagione.anno}.</div>
        </div>
      </MobileShell>
    );
  }

  return (
    <MobileShell logoSuffix="REGOLE">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Regole")}</h2>
        <div className="text-sm text-fg-muted">
          {tr("Stagione")} <span className="text-fg font-bold">{stagione.anno}</span>
          {reg.congelato_at && (
            <span className="ml-2 pill bg-danger/20 text-danger">{tr("congelato")}</span>
          )}
        </div>
      </div>

      {/* Andrea 2026-09-17: Mantra e' di Fantacalcio.it, va detto. */}
      {stagione.modalita === "mantra" && <AvvisoMantra />}

      <Section title={tr("Bonus / Malus")}>
        {reg.bonus_malus.map((b) => (
          <Row
            key={b.evento}
            label={EVENTO_LABEL[b.evento] ?? etichetta(b.evento)}
            value={tr("{{v1}}{{v2}}", { v1: Number(b.punteggio) > 0 ? "+" : "", v2: b.punteggio })}
          />
        ))}
      </Section>

      <Section title={tr("Soglie gol da fantapunti")}>
        {reg.soglie_gol.length === 0 ? (
          <div className="text-xs text-fg-muted">{tr("Nessuna soglia configurata.")}</div>
        ) : (
          reg.soglie_gol.map((s, i) => (
            <Row
              key={i}
              label={tr("Da {{v1}} punti", { v1: s.punteggio_minimo })}
              value={tr("{{v1}} gol", { v1: s.gol_assegnati })}
            />
          ))
        )}
      </Section>

      {/* Andrea 2026-09-10: il fattore capitano ora puo' essere un
          malus, quindi niente "+" davanti a tutto, e le soglie si
          leggono come fasce ("<= 4", "5.5", ">= 8"). */}
      <Section title={tr("Fattore capitano")}>
        {reg.bonus_capitano.length === 0 ? (
          <div className="text-xs text-fg-muted">{tr("Nessun fattore capitano.")}</div>
        ) : (
          (() => {
            const righe = [...reg.bonus_capitano].sort(
              (a, b) => Number(a.voto_base_minimo) - Number(b.voto_base_minimo),
            );
            const fasce = etichetteFasce(righe.map((c) => Number(c.voto_base_minimo)));
            return righe.map((c, i) => (
              <Row key={i} label={`Voto capitano ${fasce[i]}`} value={conSegno(c.bonus_capitano)} />
            ));
          })()
        )}
      </Section>

      {reg.modificatore_difesa_attivo && (
        <Section title={tr("{{v1}} · modificatore difesa", { v1: NOME_BONUS_DIFENSIVO })}>
          <Row label="Min. difensori" value={reg.modificatore_difesa_min_difensori} />
          <Row
            label={tr("Ruoli difesa ({{v1}})", { v1: NOME_MODALITA_PRO })}
            value={sigleRuoli(reg.mantra_ruoli_difesa, ", ")}
          />
          {/* Andrea 2026-09-11: si deve leggere se nella media entra
              anche il portiere. */}
          <Row
            label="Portiere nella media"
            value={
              reg.modificatore_difesa_includi_portiere
                ? tr("Sì, il voto del portiere fa media")
                : tr("No, solo i difensori")
            }
          />
          {(() => {
            const righe = [...reg.modificatore_difesa].sort(
              (a, b) => Number(a.media_voto_minimo) - Number(b.media_voto_minimo),
            );
            const fasce = etichetteFasce(righe.map((m) => Number(m.media_voto_minimo)));
            return righe.map((m, i) => (
              <Row key={i} label={`Media difesa ${fasce[i]}`} value={conSegno(m.bonus)} />
            ));
          })()}
        </Section>
      )}

      {reg.fairplay_attivo && reg.fairplay.length > 0 && (
        <Section title={tr("Fairplay")}>
          {reg.fairplay.map((f, i) => (
            <Row key={i} label={f.descrizione} value={`+${f.bonus}`} />
          ))}
        </Section>
      )}

      <Section title={tr("Formazione & sostituzioni")}>
        <Row label="Struttura panchina" value={etichetta(reg.panchina_struttura)} />
        {reg.max_sostituzioni !== null && (
          <Row label="Max sostituzioni" value={reg.max_sostituzioni} />
        )}
        <Row label="Comportamento SV" value={etichetta(reg.sv_comportamento)} />
        <Row label="Voto rinvio Serie A" value={etichetta(reg.voto_giocatori_rinvio_sa)} />
        <Row
          label="Formazione non schierata"
          value={
            etichetta(reg.formazione_non_schierata) +
            (reg.formazione_non_schierata_punteggio_fisso
              ? tr(" ({{v1}})", { v1: reg.formazione_non_schierata_punteggio_fisso })
              : "")
          }
        />
      </Section>

      <Section title={tr("Spareggi KO")}>
        <Row label="Tipo spareggio" value={etichetta(reg.spareggio_ko)} />
        {reg.spareggio_supplementari_attivo && (
          <>
            <Row label="Supplementari" value={etichetta(reg.spareggio_supplementari_modalita)} />
            <Row
              label="Panchinari supplementari"
              value={reg.spareggio_supplementari_n_panchinari}
            />
          </>
        )}
      </Section>
    </MobileShell>
  );
}
